import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRole } from "../../contexts/RoleContext";
import { setStaffCaregiverType } from "./staffNotesService";
export default function StaffCaregiverSettings({ child, canManage }) {
  const { refreshRoles } = useRole();
  const [open, setOpen] = useState(false),
    [members, setMembers] = useState(child.staffCaregivers || {});
  const [timeZone, setTimeZone] = useState(
    child.timeZone ||
      child.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [reminderDays, setReminderDays] = useState(
    child.staffNotePolicy?.reminderDays || "",
  );
  const [busy, setBusy] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    setMembers(child.staffCaregivers || {});
  }, [child.id, child.staffCaregivers]);
  if (!canManage) return null;
  const save = async (id) => {
    setBusy(id);
    setError("");
    try {
      await setStaffCaregiverType({
        childId: child.id,
        caregiverId: id,
        enabled: members[id]?.enabled === true,
        providerTitle: members[id]?.providerTitle || "",
        timeZone,
        reminderDays: reminderDays === "" ? null : Number(reminderDays),
      });
      await refreshRoles();
    } catch (error) {
      setError(error.message || "The caregiver type was not saved.");
    } finally {
      setBusy("");
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
      <Button onClick={() => setOpen((value) => !value)}>
        Caregiver types and note signing
      </Button>
      <Collapse in={open}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            Staff caregivers have the same shared-profile permissions as
            Caregivers, plus signing their own original notes. Parents and other
            caregivers keep their current workflow.
          </Typography>
          <TextField
            label="Profile timezone"
            value={timeZone}
            disabled={Boolean(child.timeZone || child.timezone)}
            onChange={(event) => setTimeZone(event.target.value)}
          />
          <TextField
            label="Signing reminder days (optional home policy)"
            type="number"
            value={reminderDays}
            onChange={(event) => setReminderDays(event.target.value)}
            helperText="Enter the number of calendar days in your home's signing policy. Leave blank to show age only."
          />
          {(child.users?.caregivers || []).map((member) => {
            const id =
              typeof member === "string" ? member : member.uid || member.id;
            if (!id) return null;
            const current = members[id] || {
              enabled: false,
              providerTitle: "",
            };
            return (
              <Stack key={id} spacing={1}>
                <Typography fontWeight={700}>
                  {member.displayName ||
                    member.name ||
                    member.email ||
                    "Caregiver"}
                </Typography>
                <TextField
                  select
                  label="Caregiver type"
                  value={current.enabled ? "staff" : "ordinary"}
                  onChange={(event) =>
                    setMembers((previous) => ({
                      ...previous,
                      [id]: {
                        ...current,
                        enabled: event.target.value === "staff",
                      },
                    }))
                  }
                >
                  <MenuItem value="ordinary">Caregiver</MenuItem>
                  <MenuItem value="staff">
                    Staff caregiver — signs original notes
                  </MenuItem>
                </TextField>
                {current.enabled ? (
                  <TextField
                    label="Staff service-provider title"
                    value={current.providerTitle}
                    onChange={(event) =>
                      setMembers((previous) => ({
                        ...previous,
                        [id]: { ...current, providerTitle: event.target.value },
                      }))
                    }
                  />
                ) : null}
                <Button disabled={Boolean(busy)} onClick={() => save(id)}>
                  {busy === id ? "Saving…" : "Save caregiver type"}
                </Button>
              </Stack>
            );
          })}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </Collapse>
    </Paper>
  );
}
